import type { Query } from '@feathersjs/feathers';

import BluebirdPromise from 'bluebird';
import _ from 'lodash';

import { service } from '#/shared/app';

/** How many per-id requests run at once when replacing a bulk (no-id) call. */
export const PER_ID_CONCURRENCY = 5;

/**
 * Runs `fn` once per id, a few at a time. Every id is tried even if some fail.
 *
 * If any call fails, throws one `Error` that says how many failed and carries the
 * first failure's message, so callers keep their existing catch / snackbar handling.
 *
 * Use it instead of no-id (multi) `patch` / `remove` calls, which the API is
 * going to reject from outside callers.
 */
export async function runForEachId<R>(
  ids: string[],
  fn: (id: string) => Promise<R>,
  {
    action,
    concurrency = PER_ID_CONCURRENCY,
  }: { action: string; concurrency?: number },
): Promise<R[]> {
  const results: R[] = [];
  const errors: Error[] = [];

  await BluebirdPromise.map(
    _.uniq(_.compact(ids)),
    async (id) => {
      try {
        results.push(await fn(id));
      } catch (err) {
        errors.push(err);
      }
    },
    { concurrency },
  );

  if (!_.isEmpty(errors)) {
    const total = _.size(results) + _.size(errors);
    throw new Error(
      `Failed to ${action} ${_.size(errors)} of ${total}: ${
        errors[0]?.message ?? errors[0]
      }`,
    );
  }

  return results;
}

/**
 * Pages through `serviceName` and returns the ids of every record matching `query`.
 * `pageSize` should not exceed the service's `paginate.max`.
 */
export async function findIds(
  serviceName: string,
  query: Query,
  {
    idField = 'uuid',
    pageSize = 100,
  }: { idField?: string; pageSize?: number } = {},
): Promise<string[]> {
  const ids: string[] = [];
  let total = Infinity;
  while (ids.length < total) {
    // eslint-disable-next-line no-await-in-loop -- each page's $skip depends on the last
    const res = await service(serviceName).find({
      query: {
        ...query,
        $limit: pageSize,
        $select: [idField],
        $skip: ids.length,
      },
    });
    const page = _.map(res.data, idField);
    if (_.isEmpty(page)) break;
    ids.push(...page);
    total = res.total;
  }

  return ids;
}
